import { useCallback, useEffect, useState } from "react";
import { Check, Plus, Power, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";

const mono = "font-mono2 text-[10px] uppercase tracking-wider";
const inputCls = "rounded-md border border-[#1F293D] bg-[#0B0E14] px-2 py-1.5 text-xs text-slate-200 focus:border-[#00FF87] focus:outline-none";
const EMPTY = { code: "", kind: "first_month_free", value: 0, plans: "both", recurring: false, max_redemptions: "", once_per_customer: false, starts_at: "", expires_at: "" };

const label = (p, t) => {
  if (p.kind === "annual_price" && p.target_price) return `${p.target_price} €/${t("promo.year")}`;
  if (p.percent_off) return `-${p.percent_off}%`;
  if (p.amount_off) return `-${p.amount_off} €`;
  return "—";
};

export default function PromoCodesPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/promos");
      setRows(r.data.promos || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/promos", {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value) || 0,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        starts_at: form.starts_at || null,
        expires_at: form.expires_at || null,
      });
      toast.success(t("promo.created_ok"));
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/promos/${p.id}`, { active: !p.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (p) => {
    try {
      const r = await api.delete(`/admin/promos/${p.id}`);
      toast.success(r.data.deleted ? t("promo.deleted") : t("promo.deactivated_instead"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="promo-panel">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("promo.title")}</h2>
        <button data-testid="promo-create-open" onClick={() => setOpen((o) => !o)} className={`${mono} flex items-center gap-1.5 rounded-md border border-[#00FF87]/60 px-3 py-1.5 font-bold text-[#00FF87] hover:bg-[#00FF87]/10`}>
          {open ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />} {t("promo.create")}
        </button>
      </div>
      <p className="mb-3 text-[11px] text-slate-500">{t("promo.stripe_hint")} {t("promo.archived_note")}</p>

      {open && (
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-[#1F293D] p-3 sm:grid-cols-3" data-testid="promo-create-form">
          <input data-testid="promo-code-input" className={inputCls} placeholder={t("promo.code")} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          <select data-testid="promo-kind-select" className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {["first_month_free", "percent", "amount", "annual_price"].map((k) => <option key={k} value={k}>{t(`promo.kind_${k}`)}</option>)}
          </select>
          <input data-testid="promo-value-input" className={inputCls} type="number" min="0" step="0.01" disabled={form.kind === "first_month_free"} placeholder={t(`promo.value_${form.kind}`)} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <select data-testid="promo-plans-select" className={inputCls} value={form.plans} onChange={(e) => setForm({ ...form, plans: e.target.value })}>
            {["both", "monthly", "annual"].map((p) => <option key={p} value={p}>{t(`promo.plans_${p}`)}</option>)}
          </select>
          <input data-testid="promo-max-input" className={inputCls} type="number" min="1" placeholder={t("promo.max_redemptions")} value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} />
          <label className={`${mono} flex items-center gap-2 text-slate-400`}>
            <input data-testid="promo-recurring-checkbox" type="checkbox" className="h-3.5 w-3.5 accent-[#00FF87]" disabled={form.kind === "first_month_free" || form.kind === "annual_price"} checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
            {t("promo.recurring")}
          </label>
          <label className={`${mono} flex items-center gap-2 text-slate-400`}>
            <input data-testid="promo-once-checkbox" type="checkbox" className="h-3.5 w-3.5 accent-[#00FF87]" checked={form.once_per_customer} onChange={(e) => setForm({ ...form, once_per_customer: e.target.checked })} />
            {t("promo.once_per_customer")}
          </label>
          <label className={`${mono} flex items-center gap-2 text-slate-500`}>{t("promo.starts_at")}
            <input data-testid="promo-starts-input" type="date" className={inputCls} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
          </label>
          <label className={`${mono} flex items-center gap-2 text-slate-500`}>{t("promo.expires_at")}
            <input data-testid="promo-expires-input" type="date" className={inputCls} value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </label>
          <button data-testid="promo-create-submit" onClick={create} disabled={busy || form.code.trim().length < 3} className="btn-volt sm:col-span-3 disabled:opacity-50">
            {busy ? t("common.loading") : t("promo.create")}
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-left" data-testid="promo-table">
          <thead>
            <tr className={`${mono} border-b border-[#1F293D] text-slate-500`}>
              <th className="py-2">{t("promo.code")}</th>
              <th className="py-2">{t("promo.kind")}</th>
              <th className="py-2">{t("promo.value")}</th>
              <th className="py-2">{t("promo.plans")}</th>
              <th className="py-2">{t("promo.state")}</th>
              <th className="py-2">{t("promo.created")}</th>
              <th className="py-2">{t("promo.expires_at")}</th>
              <th className="py-2">{t("promo.uses")}</th>
              <th className="py-2">{t("promo.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="py-3 text-xs text-slate-500" data-testid="promo-empty">{t("promo.empty")}</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-[#1F293D]/60 text-xs text-slate-300" data-testid={`promo-row-${p.code}`}>
                <td className="py-2 font-mono2 font-bold text-slate-100">{p.code}</td>
                <td className="py-2">{t(`promo.kind_${p.kind}`)}{p.recurring ? ` · ${t("promo.recurring")}` : ""}</td>
                <td className="py-2">{label(p, t)}</td>
                <td className="py-2">{t(`promo.plans_${p.plans}`)}</td>
                <td className="py-2">
                  <span className={`${mono} font-bold ${p.active ? "text-[#00FF87]" : "text-slate-500"}`} data-testid={`promo-state-${p.code}`}>
                    {p.active ? t("promo.active") : t("promo.inactive")}
                  </span>
                </td>
                <td className="py-2 font-mono2 text-[10px] text-slate-500">{(p.created_at || "").slice(0, 10) || "—"}</td>
                <td className="py-2 font-mono2 text-[10px] text-slate-500">{(p.expires_at || "").slice(0, 10) || "—"}</td>
                <td className="py-2 font-mono2 text-[10px]">{p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ` / ${t("promo.unlimited")}`}{p.once_per_customer ? ` · ${t("promo.once_short")}` : ""}</td>
                <td className="py-2">
                  <span className="flex items-center gap-2">
                    <button data-testid={`promo-toggle-${p.code}`} onClick={() => toggle(p)} title={p.active ? t("promo.deactivate") : t("promo.activate")} className="rounded border border-[#1F293D] p-1 text-slate-400 hover:border-[#00FF87]/60 hover:text-[#00FF87]">
                      {p.active ? <Power className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                    </button>
                    <button data-testid={`promo-delete-${p.code}`} onClick={() => remove(p)} title={t("promo.delete")} className="rounded border border-[#1F293D] p-1 text-slate-400 hover:border-red-500/60 hover:text-red-400">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
